import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { differenceInCalendarDays } from 'date-fns';
// components
import { Badge } from '../components/badge';
// mui components
import { Grid, Typography } from '@mui/material';
// api
import { useCreateOverdueItemsViewsMutation } from 'redux-store/api/activity-feed';
import {
  IOverdueItem,
  OverdueItemTypeEnum,
  useGetOverdueItemsQuery,
} from 'redux-store/api/projects';
// icons
import AttentionIcon from 'assets/icons/Alert_icon_new.svg';
import AttentionIconWithDot from 'assets/icons/alert-with-dot-icon.svg';
// styles
import { styles } from './styles';
import { useSidePanel } from 'hooks/useSidePanel';
import { useSyncSelectedProject } from 'hooks/useSyncSelectedProject';

interface IAlertButtonProps {
  projectId?: number;
  itemsCount?: number;
  unviewedItemsCount?: number;
}

export const AlertButton = ({
  projectId,
  itemsCount = 0,
  unviewedItemsCount = 0,
}: IAlertButtonProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { setProjectId: setProjectIdCtx } = useSidePanel();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);
  const alertButtonId = open ? 'alert-button-popover' : undefined;

  const { data: overdueItems, isLoading } = useGetOverdueItemsQuery(
    { projectId: Number(projectId) },
    { skip: !open }
  );

  // TODO: Check whether useSyncSelectedProject(projectId) can be safely removed
  // useSyncSelectedProject(projectId);

  const [bulkUpdate] = useCreateOverdueItemsViewsMutation();

  useEffect(() => {
    if (open) {
      setProjectIdCtx(projectId);
    }
  }, [projectId, open]);

  const markItemsAsViewed = () => {
    const hasUnviewedItems = unviewedItemsCount && overdueItems?.overdue?.length;
    if (!hasUnviewedItems) return;

    const groupByType = (type: OverdueItemTypeEnum) =>
      overdueItems.overdue
        .filter(({ type: itemType, isViewed }) => !isViewed && itemType === type)
        .map(({ id }) => id);

    const itemIds = groupByType(OverdueItemTypeEnum.PHASE);
    const selectionItemIds = groupByType(OverdueItemTypeEnum.SELECTION);

    if (itemIds.length || selectionItemIds.length) {
      bulkUpdate({ projectId: Number(projectId), itemIds, selectionItemIds });
    }
  };

  const onOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const onClose = () => {
    markItemsAsViewed();
    setAnchorEl(null);
  };

  const onSeeMore = () => {
    onClose();
    navigate(`/project-dashboard/${projectId}?status=OVERDUE`);
  };

  const onItemClick = (overdueItem: IOverdueItem) => () => {
    onClose();

    const { type, id } = overdueItem;
    const currentPath = location.pathname.split('/')[1];

    const isPhase = type === OverdueItemTypeEnum.PHASE;
    const isSelection = type === OverdueItemTypeEnum.SELECTION;

    const isProjectDashboard = currentPath === 'project-dashboard' && isPhase;
    const isSelections = currentPath === 'selections' && isSelection;

    if (isSelections) return;

    if (isProjectDashboard) {
      document
        .getElementById(id.toString())
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    const path = isPhase ? 'project-dashboard' : 'selections';
    const hash = isPhase ? `#${id}` : '';
    navigate(`/${path}/${projectId}${hash}`);
  };

  const hasData = overdueItems?.overdue?.length > 0;
  const isNoContentVisible = !hasData && open && !isLoading;
  const isLoadingVisible = !hasData && open && isLoading;
  const isContentVisible = hasData && open;

  return (
    <Badge
      id={alertButtonId}
      open={open}
      buttonProps={{
        title: `${itemsCount ?? 0}`,
        icon: unviewedItemsCount ? <AttentionIconWithDot /> : <AttentionIcon />,
      }}
      popoverProps={{ anchorEl }}
      onOpen={onOpen}
      onClose={onClose}
    >
      <Badge.Header heading="Recent Overdue Items" actionText="See all" onActionClick={onSeeMore} />
      <Badge.Container additionalSx={styles.badgeContainer(isContentVisible)}>
        {isNoContentVisible && <Badge.NoContent heading="No Overdue Items" />}
        {isLoadingVisible && <Badge.Loading />}
        {isContentVisible && (
          <>
            {overdueItems?.overdue?.map((overdueItem) => {
              const { title, id, endDate, isViewed } = overdueItem;
              const daysDiff = differenceInCalendarDays(new Date(), new Date(endDate));
              const overdueDays = daysDiff === 0 ? 1 : daysDiff;

              return (
                <Grid key={id} sx={styles.paperItem}>
                  <Grid sx={styles.redDotContainer}>
                    {!isViewed && <Grid sx={styles.redDot}></Grid>}
                  </Grid>
                  <Grid sx={styles.itemContentContainer}>
                    <Typography sx={styles.itemContent}>
                      <span onClick={onItemClick(overdueItem)}>{title}</span>{' '}
                      {`is now ${overdueDays} ${overdueDays === 1 ? 'day' : 'days'} overdue`}
                    </Typography>
                  </Grid>
                </Grid>
              );
            })}
          </>
        )}
      </Badge.Container>
    </Badge>
  );
};
